import * as React from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import HistoryIcon from "@mui/icons-material/History";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import axios from "axios";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import Wrapper from "../../../components/TenantWrapper";
import title from "../../../components/title";
import { BASE_URL } from "../../../config/config";
import { useTheme as useAppTheme } from "../../../context/ThemeContext";

const money = (value) => `Rs ${Number(value || 0).toLocaleString()}`;

const STATUS_CHIP = {
  paid: { label: "Paid", color: "success" },
  partial: { label: "Partly paid", color: "warning" },
  unpaid: { label: "Unpaid", color: "error" },
};

const BILL_STATUS_CHIP = {
  approved: { label: "Paid", color: "success" },
  rejected: { label: "Rejected", color: "error" },
  pending: { label: "Pending", color: "warning" },
};

function CycleRow({ cycle, defaultOpen }) {
  const [open, setOpen] = React.useState(Boolean(defaultOpen));
  const chip = STATUS_CHIP[cycle.status] || STATUS_CHIP.unpaid;

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2,
        borderRadius: 3,
        border: "1px solid rgba(18,32,58,0.10)",
        overflow: "hidden",
      }}
    >
      <Box
        onClick={() => setOpen((prev) => !prev)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2,
          py: 1.75,
          cursor: "pointer",
          "&:hover": { bgcolor: "rgba(18,32,58,0.03)" },
        }}
      >
        <ReceiptLongIcon color="primary" />

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography sx={{ fontWeight: 700 }}>{cycle.cycleLabel}</Typography>
            {cycle.isCurrent && (
              <Chip size="small" label="Current cycle" variant="outlined" />
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {cycle.billCount} bill{cycle.billCount === 1 ? "" : "s"} ·{" "}
            {cycle.settledCount} paid
          </Typography>
        </Box>

        <Box sx={{ textAlign: "right" }}>
          <Typography sx={{ fontWeight: 800 }}>{money(cycle.total)}</Typography>
          <Chip size="small" label={chip.label} color={chip.color} />
        </Box>

        <IconButton size="small">
          {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>
      </Box>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Divider />
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Bill</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="right">Late fee</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell>Due</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Copy</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cycle.bills.map((bill) => {
                const status =
                  BILL_STATUS_CHIP[bill.status] || BILL_STATUS_CHIP.pending;

                return (
                  <TableRow key={bill.billId}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {bill.label}
                      </Typography>
                      {bill.source === "auto" && (
                        <Typography variant="caption" color="text.secondary">
                          Auto-generated
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">{money(bill.amount)}</TableCell>
                    <TableCell align="right">
                      {bill.penalty > 0 ? (
                        <Typography variant="body2" color="error.main">
                          +{money(bill.penalty)}
                          <br />
                          <Typography variant="caption" component="span">
                            {bill.daysOverdue}d late
                          </Typography>
                        </Typography>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {money(bill.payable)}
                    </TableCell>
                    <TableCell>{bill.dueDate || "—"}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={status.label}
                        color={status.color}
                      />
                      {bill.rejectionReason && (
                        <Typography
                          variant="caption"
                          color="error.main"
                          display="block"
                        >
                          {bill.rejectionReason}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {bill.image ? (
                        <IconButton
                          size="small"
                          href={bill.image}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </Collapse>
    </Paper>
  );
}

export default function PreviousBill() {
  title("Bill History");
  const { theme: appTheme } = useAppTheme();
  const open = useSelector((state) => state.sidebar.open);
  const { flatId } = useParams();

  const tenantId =
    localStorage.getItem("user_id") || localStorage.getItem("userId");

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [history, setHistory] = React.useState(null);

  React.useEffect(() => {
    if (!tenantId || !flatId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    axios
      .get(`${BASE_URL}/tenant-bill-history/${tenantId}/${flatId}`)
      .then((res) => {
        if (cancelled) return;
        setHistory(res?.data || null);
        setError("");
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err?.response?.data?.message ||
            "Failed to load bill history. Please try again later."
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [tenantId, flatId]);

  const cycles = history?.cycles || [];

  return (
    <>
      <Wrapper open={open} />

      <div className={`${open ? "sidebar-open" : "sidebar-closed"}`}>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
            <HistoryIcon sx={{ color: appTheme.primary }} />
            <Typography variant="h5" fontWeight={800}>
              Bill History
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Each month's billing cycle runs from the 1st to the 1st.
            {history?.tenancyStartedOn
              ? ` Your tenancy started on ${history.tenancyStartedOn}.`
              : ""}
          </Typography>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : !flatId ? (
            <Alert severity="info">
              Please open this page through your flat — go to "My Rent Flat"
              and select "View History".
            </Alert>
          ) : cycles.length === 0 ? (
            <Alert severity="info" icon={<ReceiptLongIcon />}>
              {history?.emptyStateMessage || "No bills have been generated yet."}
            </Alert>
          ) : (
            cycles.map((cycle, index) => (
              <CycleRow
                key={cycle.cycleKey}
                cycle={cycle}
                defaultOpen={index === 0}
              />
            ))
          )}
        </Container>
      </div>
    </>
  );
}
